import { computed, ComputedRef, inject, InjectionKey, ref } from "vue";
import { getSvg } from "../../../../skyward.ui.svg";
import { Dictionary } from "../../../../types/util/dictionary";
import { registerStore } from "../../core/store";
import { State } from "../../core/store/types";
import { dashboardItemStoreFeatures } from "./features";
import { DashboardItemSetup, DashboardItemStoreFeatures, DashboardItemStoreState } from "./types";

export const dashboardItemStoreKey: InjectionKey<DashboardItemStoreState<any, any>> = Symbol("dashboardItemStore");
export const dashboardItemStore = registerStore<DashboardItemStoreFeatures, ReturnType<typeof setupStore>, ReturnType<typeof setupState>>(dashboardItemStoreKey, dashboardItemStoreFeatures, setupStore, setupState);
export function isDashboardItemStore(state: State<{}, {}, {}> | undefined): state is DashboardItemStoreState<{}, {}>
{
    return state?.storeKey === String(dashboardItemStoreKey);
}

function setupStore()
{
    let selectedDashboardItemId = ref();

    return {
        get selectedDashboardItemId() { return selectedDashboardItemId.value; },
        set selectedDashboardItemId(value)
        {
            if (value)
            {
                const widgetState = dashboardItemStore.states.get(value);
                if (!widgetState)
                {
                    throw new Error(`DashboardItem with id=${value} can't be selected because it doesn't exist in the states collection.`);
                }
            }

            selectedDashboardItemId.value = value;
        },
        getDashboardItemState,
        injectDashboardItemState,
    };

    async function getDashboardItemState<TFeatureTypes, TDataType extends object = Dictionary<string, any>>(dashboardItemID: string)
    {
        return dashboardItemStore.getStateAsync<TFeatureTypes, TDataType, {}>(dashboardItemID);
    }

    function injectDashboardItemState<TFeatureTypes, TDataType extends object = Dictionary<string, any>>(): DashboardItemStoreState<TFeatureTypes, TDataType>
    {
        return inject(dashboardItemStoreKey, undefined) as DashboardItemStoreState<TFeatureTypes, TDataType>;
    }
}

function setupState(dashboardItemID: string, storeState: State<DashboardItemStoreFeatures, {}, {}>): DashboardItemSetup
{
    const classNames =
    {
        vueDashboardItem: "vueDashboardItem",
        messageContainer: "vueDashboardItemMessage__container",
        messageIsBusy: "vueDashboardItemMessage__container--isbusy",
        messageBanner: "vueDashboardItemMessage__banner",
        messageGlass: "vueDashboardItemMessage__glass",
        messageBusy: "vueDashboardItemMessage__busy",
        messageBannerSuccess: "vueDashboardItemMessage__banner--success",
        messageBannerError: "vueDashboardItemMessage__banner--error",
        messageSvg: "vueDashboardItemMessage__svg",
        messageText: "vueDashboardItemMessage__text",
    };

    const hasBackingScreen: ComputedRef<boolean> = computed(() => !!storeState.features.settings.backingModule && !!storeState.features.settings.backingObject && !!storeState.features.settings.backingScreen);
    const messageFadeSpeed = 230;
    const messageVisibleDuration = 1500; // 1.5 seconds
    let onDownloadImage: (() => void) | undefined;
    const rawDashboardItemID = computed(() => dashboardItemID.replace('dashboardItem_', ''));

    const state =
    {
        get dashboardItemID() { return dashboardItemID; },
        get rawDashboardItemID() { return rawDashboardItemID.value },
        get hasBackingScreen() { return hasBackingScreen.value; },
        set onDownloadImage(callback: () => void) { onDownloadImage = callback; },
        get isSelected() { return dashboardItemStore.selectedDashboardItemId === dashboardItemID; },
        set isSelected(value)
        {
            if (value)
            {
                dashboardItemStore.selectedDashboardItemId = dashboardItemID;
            }
            else if (dashboardItemStore.selectedDashboardItemId === dashboardItemID)
            {
                dashboardItemStore.selectedDashboardItemId = null;
            }
        },
        setMessageBusy(isBusy: boolean)
        {
            const deferred = $.Deferred();
            const messageContainer = getMessageContainer();
            window.requestAnimationFrame(() =>
            {
                messageContainer.toggleClass(classNames.messageIsBusy, isBusy);
                deferred.resolve();
            });
            return deferred.promise();
            
        },
        loadedMedia: {},
        showSuccessMessage(messageText: string)
        {
            const deferred = $.Deferred();
            this.setMessageBusy(false).then(() =>
            {
                showMessage(messageText, true).then(() => deferred.resolve());
            });
            return deferred.promise();
        },
        showErrorMessage(messageText: string)
        {
            const deferred = $.Deferred();
            this.setMessageBusy(false).then(() =>
            {
                showMessage(messageText, false).then(() => deferred.resolve());
            });
            return deferred.promise();
        },
        tryDownloadImage()
        {
            if (onDownloadImage)
            {
                onDownloadImage();
            }
        }
    };

    return state;

    function getMessageContainer()
    {
        const container = $(`#${dashboardItemID}`);
        let messageContainer = container.find(`.${classNames.messageContainer}`);
        if (messageContainer.length)
        {
            return messageContainer;
        }
        return initMessageElements(container);
    }

    function initMessageElements(container: JQuery<HTMLElement>)
    {
        const messageGlass = $("<div>", { class: classNames.messageGlass });
        const messageBusy = $("<div>", { class: classNames.messageBusy });

        return $("<div>",
        {
            class: classNames.messageContainer,
            append: [messageGlass, messageBusy]
        }).prependTo(container);
    }

    function showMessage(messageText: string, isSuccess: boolean)
    {
        const deferred = $.Deferred();
        const messageContainer = getMessageContainer();
        const containerHeight = messageContainer.height() || 600;

        const messageBanner = $("<div>",
            {
                class: classNames.messageBanner,
                css: { marginTop: `-${containerHeight + 60}px` },
                append: [
                    getSvg(isSuccess ? "Check" : "Error").addClass(classNames.messageSvg),
                    $("<span>", { class: classNames.messageText, text: messageText })
                ]
            });

        messageBanner.toggleClass(classNames.messageBannerSuccess, isSuccess);
        messageBanner.toggleClass(classNames.messageBannerError, !isSuccess);

        messageBanner.appendTo(messageContainer);

        messageBanner.animate({ "margin-top": 0 }, 600, "easeOutBack", () => 
        {
            window.setTimeout(() =>
            {
                if (messageContainer.find(`.${classNames.messageBanner}`).length === 1)
                {
                    messageContainer.fadeOut(messageFadeSpeed, () =>
                    {
                        messageContainer.remove();
                        deferred.resolve();
                    });
                }
                else
                {
                    messageBanner.fadeOut(messageFadeSpeed, () =>
                    {
                        messageBanner.remove();                        
                        deferred.resolve();
                    });
                }
            }, messageVisibleDuration);
        });

        return deferred.promise();
    }
}
